import {
  BALANCE,
  damageForTile,
  type BattlePlayerState,
  type BattleRoomState,
  type GameInput,
  type MatchResult,
  type MatchResetInfo,
  type PhaseEnvelope,
  type RuleEvent,
  type Transition,
  type TransitionMessage,
} from '@2k48/shared';
import { feedback } from '../../platform/feedback';
import type { MatchSession, SessionHandlers } from '../../net/client';
import { CombatEffects, type CombatSide } from '../CombatEffects';
import { mountGameArea, type GameAreaHandle } from '../GameArea';
import type { DebugEffect } from '../debug/effects';
import { onDebugEffect } from '../debug/effects';
import { DEBUG_ENABLED } from '../debug/metrics';
import { attachTransitionFeedback } from '../transitionFeedback';
import {
  playerFingerprint,
  predictInput,
  replayPending,
  roomStateToBattleState,
} from './prediction';

export interface NetworkPlayerUi {
  readonly seat: number;
  readonly name: string;
  readonly hp: number;
  readonly score: number;
  readonly combo: number;
  readonly maxCombo: number;
  readonly maxTile: number;
  readonly connected: boolean;
}

export interface NetworkGameUiState {
  readonly players: readonly NetworkPlayerUi[];
  readonly localSeat: number;
  readonly modeId: string;
  readonly roomCode: string;
}

export interface NetworkCombatUiState {
  readonly attackerSeat: number;
  readonly targetSeat: number;
  readonly damage: number;
  readonly powerValue?: 512 | 1024 | 2048;
}

export interface NetworkGameOptions {
  combatRoot?: HTMLElement;
  onState?(state: NetworkGameUiState): void;
  onPhase?(envelope: PhaseEnvelope): void;
  onPhaseRefresh?(envelope: PhaseEnvelope): void;
  onProgress?(remainingMs: number, envelope: PhaseEnvelope): void;
  onResult?(result: MatchResult): void;
  /** Ids only; the names live in the shared catalogue the client already has. */
  onAchievements?(ids: readonly string[]): void;
  onCombat?(state: NetworkCombatUiState | null): void;
  onReset?(info: MatchResetInfo): void;
  onDrop?(reconnectBy: number | null): void;
  onReconnect?(): void;
  onError?(error: unknown): void;
}

/**
 * Mobile network play seam: Svelte owns the HUD, this class owns the hot path.
 * Gestures are predicted with the shared pure rules, acknowledged by sequence,
 * and reconciled against compact schema patches without rebuilding the board.
 */
export class NetworkBattleController {
  readonly area: GameAreaHandle;
  readonly combat: CombatEffects | null;
  readonly handlers: SessionHandlers;

  private session: MatchSession | null = null;
  private authoritative: ReturnType<typeof roomStateToBattleState> | null = null;
  private optimistic: ReturnType<typeof roomStateToBattleState> | null = null;
  private latestRoom: BattleRoomState | null = null;
  private readonly pending = new Map<number, GameInput>();
  private readonly predicted = new Set<number>();
  private readonly sentAt = new Map<number, number>();
  private readonly visibleHp = new Map<number, number>();
  private lastTransitionSeq = 0;
  private visualFingerprint = '';
  private localSeat = 0;
  private phase = 'lobby';
  private destroyed = false;
  private unsubscribeDebug: (() => void) | null = null;

  constructor(
    container: HTMLElement,
    private readonly options: NetworkGameOptions = {},
  ) {
    this.area = mountGameArea(container, {
      cols: BALANCE.board.size,
      rows: BALANCE.board.size,
      defaultTone: 'p1',
      onDirection: (direction) => this.onDirection(direction),
    });
    this.area.input.setEnabled(false);
    this.combat = options.combatRoot
      ? new CombatEffects(options.combatRoot, this.area.renderer.root)
      : null;
    this.unsubscribeDebug = DEBUG_ENABLED
      ? onDebugEffect((effect) => this.triggerDebug(effect))
      : null;

    this.handlers = {
      onJoined: (info) => {
        this.localSeat = info.seat;
      },
      onState: (state) => this.acceptState(state),
      onTransitions: (message) => this.acceptTransitions(message),
      onRejected: (message) => this.rejectPrediction(message.inputSeq),
      onResult: (result) => this.options.onResult?.(result),
      onAchievements: (message) => this.options.onAchievements?.(message.ids),
      onReset: (info) => this.reset(info),
      onEnter: (envelope) => this.setPhase(envelope, false),
      onRefresh: (envelope) => this.setPhase(envelope, true),
      onProgress: (remaining, envelope) => this.options.onProgress?.(remaining, envelope),
      onDrop: () => {
        this.area.input.setEnabled(false);
        const reconnectBy = this.session
          ? this.session.clock.serverNow() + BALANCE.phases.reconnectionGraceMs
          : null;
        this.options.onDrop?.(reconnectBy);
      },
      onReconnect: () => {
        // State patches received while offline replace missed transition
        // messages. Snap HP once here; normal live patches remain visually
        // locked until their authoritative hit event commits the drain.
        this.visibleHp.clear();
        if (this.session) this.acceptState(this.session.state);
        this.area.input.setEnabled(this.phase === 'playing');
        this.options.onReconnect?.();
      },
      onError: (error) => this.options.onError?.(error),
      onLeave: (code) => {
        if (!this.destroyed) this.options.onError?.(new Error(`Conexion cerrada (${code}).`));
      },
    };
  }

  attach(session: MatchSession): void {
    this.session = session;
    this.localSeat = session.seat;
    this.acceptState(session.state);
    session.ready();
  }

  private setPhase(envelope: PhaseEnvelope, refresh: boolean): void {
    this.phase = envelope.phase;
    this.area.input.setEnabled(envelope.phase === 'playing');
    if (refresh) this.options.onPhaseRefresh?.(envelope);
    else this.options.onPhase?.(envelope);
  }

  private onDirection(direction: GameInput['direction']): void {
    if (!direction || !this.session || !this.optimistic || this.phase !== 'playing') return;

    // The message goes out immediately; prediction is an animation decision,
    // never authority. A locally illegal gesture is still sent so server and
    // client sequence numbers cannot diverge.
    const input = this.session.sendDirection(direction);
    this.sentAt.set(input.seq, input.at);
    const step = predictInput(this.optimistic, input, this.latestRoom?.startAt ?? input.at);
    if (step.rejected) {
      feedback.emit('ui.invalid');
      return;
    }

    this.pending.set(input.seq, input);
    this.predicted.add(input.seq);
    this.optimistic = step.state;
    this.applyBoardTransitions(step.transitions);
    this.visualFingerprint = playerFingerprint(this.player(this.optimistic, this.localSeat));
    this.emitUi();
  }

  private acceptState(room: BattleRoomState): void {
    if (this.destroyed) return;
    this.latestRoom = room;
    const localSchema = Array.from(room.seats).find((seat) => seat.seat === this.localSeat);
    const ack = localSchema?.lastInputSeq ?? 0;
    const knownMoveTimes = new Map<number, number>();
    const ackAt = this.sentAt.get(ack);
    if (ackAt !== undefined) knownMoveTimes.set(this.localSeat, ackAt);

    this.authoritative = roomStateToBattleState(room, knownMoveTimes);
    for (const player of this.authoritative.players) {
      if (!this.visibleHp.has(player.seat)) this.visibleHp.set(player.seat, player.hp);
    }
    for (const seq of [...this.pending.keys()]) {
      if (seq <= ack) this.pending.delete(seq);
    }
    for (const seq of [...this.sentAt.keys()]) {
      if (seq < ack - 2) this.sentAt.delete(seq);
    }

    this.optimistic = replayPending(this.authoritative, [...this.pending.values()], room.startAt);
    const local = this.player(this.optimistic, this.localSeat);
    const nextFingerprint = playerFingerprint(local);
    if (nextFingerprint !== this.visualFingerprint) {
      this.area.renderer.finishRunning();
      this.area.sync(this.entities(local));
      this.visualFingerprint = nextFingerprint;
    }
    this.emitUi();
  }

  private acceptTransitions(message: TransitionMessage): void {
    if (message.seq <= this.lastTransitionSeq) return;
    this.lastTransitionSeq = message.seq;

    const wasPredicted =
      message.seat === this.localSeat &&
      message.causedBy !== undefined &&
      this.predicted.delete(message.causedBy);
    if (message.seat === this.localSeat && !wasPredicted) {
      this.applyBoardTransitions(this.localTone(message.transitions));
      // The following authoritative patch provides the exact fingerprint.
      this.visualFingerprint = '';
    }
    this.playEvents(message.events);
  }

  private rejectPrediction(inputSeq: number): void {
    if (!this.pending.delete(inputSeq) || !this.authoritative) return;
    this.predicted.delete(inputSeq);
    this.optimistic = replayPending(
      this.authoritative,
      [...this.pending.values()],
      this.latestRoom?.startAt ?? 0,
    );
    const local = this.player(this.optimistic, this.localSeat);
    this.area.renderer.finishRunning();
    this.area.sync(this.entities(local));
    this.visualFingerprint = playerFingerprint(local);
    feedback.emit('ui.invalid');
    this.emitUi();
  }

  private playEvents(events: readonly RuleEvent[]): void {
    if (!this.combat || events.length === 0) return;
    const queue = [...events].filter((event) =>
      ['attack:launched', 'board:recovered', 'battle:ko'].includes(event.type),
    );
    const next = (): void => {
      const event = queue.shift();
      if (!event || !this.combat) return;

      if (event.type === 'attack:launched') {
        const attackerSeat = event.seat ?? 0;
        const targetSeat = numberFrom(event.data?.targetSeat) ?? 0;
        const hpFrom = numberFrom(event.data?.hpFrom) ?? 100;
        const hpTo = numberFrom(event.data?.hpTo) ?? hpFrom;
        const damage = numberFrom(event.data?.damage) ?? Math.max(0, hpFrom - hpTo);
        const power = powerValue(event.data?.powerValue);
        this.visibleHp.set(targetSeat, hpFrom);
        this.emitUi();
        this.options.onCombat?.({
          attackerSeat,
          targetSeat,
          damage,
          ...(power ? { powerValue: power } : {}),
        });
        this.combat.playAttack({
          attacker: this.sideOf(attackerSeat),
          target: this.sideOf(targetSeat),
          damage,
          hpFrom,
          hpTo,
          ...(attackerSeat === this.localSeat && typeof event.data?.sourceTileId === 'string'
            ? { sourceTileId: event.data.sourceTileId }
            : {}),
          ...(power ? { powerValue: power } : {}),
          onCharge: () =>
            feedback.emit('attack.charge', { haptic: attackerSeat === this.localSeat }),
          onImpact: () => feedback.emit('attack.impact', { haptic: targetSeat === this.localSeat }),
          onHpCommit: () => {
            this.visibleHp.set(targetSeat, hpTo);
            this.emitUi();
          },
          onComplete: () => {
            this.options.onCombat?.(null);
            next();
          },
        });
        return;
      }

      if (event.type === 'board:recovered') {
        const seat = event.seat ?? this.localSeat;
        const hpFrom = numberFrom(event.data?.hpFrom) ?? 100;
        const hpTo = numberFrom(event.data?.hpTo) ?? hpFrom;
        this.visibleHp.set(seat, hpFrom);
        this.emitUi();
        this.options.onCombat?.({
          attackerSeat:
            this.latestRoom?.seats.find((candidate) => candidate.seat !== seat)?.seat ??
            this.localSeat,
          targetSeat: seat,
          damage: numberFrom(event.data?.hpPenalty) ?? Math.max(0, hpFrom - hpTo),
        });
        this.combat.playHit({
          target: this.sideOf(seat),
          damage: numberFrom(event.data?.hpPenalty) ?? Math.max(0, hpFrom - hpTo),
          hpFrom,
          hpTo,
          onImpact: () => feedback.emit('attack.impact', { haptic: seat === this.localSeat }),
          onHpCommit: () => {
            this.visibleHp.set(seat, hpTo);
            this.emitUi();
          },
          onComplete: () => {
            this.options.onCombat?.(null);
            next();
          },
        });
        return;
      }

      const knockedOut = Array.isArray(event.data?.knockedOut)
        ? event.data.knockedOut.map(numberFrom).filter((seat): seat is number => seat !== null)
        : [];
      const seat = knockedOut[0] ?? event.seat ?? this.localSeat;
      this.combat.playKo(this.sideOf(seat), next, () =>
        feedback.emit('match.ko', { haptic: seat === this.localSeat }),
      );
    };
    next();
  }

  private reset(info: MatchResetInfo): void {
    this.pending.clear();
    this.predicted.clear();
    this.sentAt.clear();
    this.visibleHp.clear();
    this.lastTransitionSeq = 0;
    this.visualFingerprint = '';
    this.authoritative = null;
    this.optimistic = null;
    this.combat?.finishRunning();
    this.options.onCombat?.(null);
    this.area.renderer.finishRunning();
    this.area.sync([]);
    this.options.onReset?.(info);
  }

  private emitUi(): void {
    const state = this.optimistic ?? this.authoritative;
    const room = this.latestRoom;
    if (!state || !room) return;
    const schemaBySeat = new Map(Array.from(room.seats).map((seat) => [seat.seat, seat]));
    const authoritativeBySeat = new Map(
      (this.authoritative?.players ?? []).map((player) => [player.seat, player]),
    );
    this.options.onState?.({
      localSeat: this.localSeat,
      modeId: room.modeId,
      roomCode: room.roomCode,
      players: state.players.map((player) => ({
        seat: player.seat,
        name: schemaBySeat.get(player.seat)?.displayName || `PLAYER ${player.seat + 1}`,
        hp:
          this.visibleHp.get(player.seat) ?? authoritativeBySeat.get(player.seat)?.hp ?? player.hp,
        score: player.score,
        combo: player.combo,
        maxCombo: player.maxCombo,
        maxTile: player.maxTile,
        connected: schemaBySeat.get(player.seat)?.connected ?? false,
      })),
    });
  }

  private player(
    state: ReturnType<typeof roomStateToBattleState>,
    seat: number,
  ): BattlePlayerState | undefined {
    return state.players.find((player) => player.seat === seat);
  }

  private entities(player: BattlePlayerState | undefined) {
    return (
      player?.board.tiles.map((tile) => ({
        id: tile.id,
        value: tile.value,
        cell: tile.cell,
        tone: 'p1',
      })) ?? []
    );
  }

  private localTone(transitions: readonly Transition[]): Transition[] {
    return transitions.map((transition) =>
      transition.kind === 'spawn' || transition.kind === 'update'
        ? { ...transition, tags: [...(transition.tags ?? []), 'p1'] }
        : transition,
    );
  }

  private sideOf(seat: number): CombatSide {
    return seat === this.localSeat ? 'p1' : 'p2';
  }

  private applyBoardTransitions(transitions: readonly Transition[]): void {
    const timeline = this.area.apply(transitions);
    attachTransitionFeedback(timeline, transitions);
  }

  triggerDebug(effect: DebugEffect): void {
    if (!this.combat) return;
    this.area.renderer.finishRunning();
    this.combat.finishRunning();

    const targetSeat =
      this.optimistic?.players.find((player) => player.seat !== this.localSeat)?.seat ?? 1;
    const battle = this.optimistic ?? this.authoritative;
    const hpOf = (seat: number): number =>
      this.visibleHp.get(seat) ?? (battle ? this.player(battle, seat)?.hp : undefined) ?? 100;

    // Debug effects are visual-only in a network match: they exercise the hot
    // render path without injecting fake authoritative gameplay messages.
    if (effect === 'hit') {
      const from = hpOf(this.localSeat);
      const to = Math.max(0, from - 10);
      this.visibleHp.set(this.localSeat, from);
      this.emitUi();
      this.options.onCombat?.({
        attackerSeat: targetSeat,
        targetSeat: this.localSeat,
        damage: from - to,
      });
      this.combat.playHit({
        target: 'p1',
        damage: from - to,
        hpFrom: from,
        hpTo: to,
        onImpact: () => feedback.emit('attack.impact'),
        onHpCommit: () => {
          this.visibleHp.set(this.localSeat, to);
          this.emitUi();
        },
        onComplete: () => this.options.onCombat?.(null),
      });
      return;
    }

    if (effect === 'ko') {
      this.visibleHp.set(targetSeat, 0);
      this.emitUi();
      this.combat.playKo('p2', undefined, () => feedback.emit('match.ko', { haptic: false }));
      return;
    }

    const value = debugValue(effect);
    const from = hpOf(targetSeat);
    const damage = damageForTile(value);
    const to = Math.max(0, from - damage);
    const source = battle
      ? this.player(battle, this.localSeat)?.board.tiles.reduce<
          BattlePlayerState['board']['tiles'][number] | undefined
        >((highest, tile) => (!highest || tile.value > highest.value ? tile : highest), undefined)
      : undefined;
    this.visibleHp.set(targetSeat, from);
    this.emitUi();
    this.options.onCombat?.({
      attackerSeat: this.localSeat,
      targetSeat,
      damage,
      ...(value >= 512 ? { powerValue: value as 512 | 1024 | 2048 } : {}),
    });
    this.combat.playAttack({
      attacker: 'p1',
      target: 'p2',
      damage,
      hpFrom: from,
      hpTo: to,
      ...(source ? { sourceTileId: source.id } : {}),
      ...(value >= 512 ? { powerValue: value as 512 | 1024 | 2048 } : {}),
      onCharge: () => feedback.emit('attack.charge'),
      onImpact: () => feedback.emit('attack.impact', { haptic: false }),
      onHpCommit: () => {
        this.visibleHp.set(targetSeat, to);
        this.emitUi();
      },
      onComplete: () => this.options.onCombat?.(null),
    });
  }

  destroy(): void {
    this.destroyed = true;
    this.pending.clear();
    this.predicted.clear();
    this.sentAt.clear();
    this.unsubscribeDebug?.();
    this.unsubscribeDebug = null;
    this.options.onCombat?.(null);
    this.combat?.destroy();
    this.area.destroy();
  }
}

function numberFrom(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function powerValue(value: unknown): 512 | 1024 | 2048 | undefined {
  return value === 512 || value === 1024 || value === 2048 ? value : undefined;
}

function debugValue(effect: Exclude<DebugEffect, 'hit' | 'ko'>): number {
  switch (effect) {
    case 'attack-8':
      return 8;
    case 'attack-64':
      return 64;
    case 'power-512':
      return 512;
    case 'power-1024':
      return 1024;
    case 'power-2048':
      return 2048;
  }
}
